import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { PdfSection, TemplateSection, CompanySection, AutoSendSection, AutomationSection } from "@/components/settings/SettingsSections";
import { TestEmailCard, PasswordCard } from "@/components/settings/SettingsCards";
import { SediSection, UsersSection } from "@/components/settings/OrgSections";
import { MailboxSection } from "@/components/settings/MailboxSection";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const OPERATOR_FIELDS = ["contracts_sender", "alert_email", "alerts_enabled", "alert_days", "send_summary", "auto_sync", "sedi"];

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = () => api.get("/settings").then((r) => setForm((f) => ({ ...r.data, smtp_password: "", pdf_password: "", imap_password: "", ...(f ? { sender_name: f.sender_name ?? r.data.sender_name } : {}) })))
    .catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { reload(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const { has_smtp_password, has_pdf_password, has_imap_password, smtp_configured, imap_configured, imap_account, historical_done, last_check_at, ...all } = form;
    const body = isAdmin ? { ...all, smtp_port: Number(all.smtp_port), contracts_months: Number(all.contracts_months) || 4, alert_days: Number(all.alert_days) || 30 }
      : Object.fromEntries(OPERATOR_FIELDS.filter((k) => k in all).map((k) => [k, k === "alert_days" ? Number(all[k]) || 30 : all[k]]));
    try {
      const { data } = await api.put("/settings", body);
      setForm({ ...data, smtp_password: "", pdf_password: "", imap_password: "" });
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <>
      <PageHeader eyebrow="Configurazione" title="Impostazioni"
        description={isAdmin ? "Collega la casella email, definisci le sedi, gestisci utenti, avvisi e cedolini." : "Come operatore puoi modificare sedi, mittenti da controllare e avvisi."}
        actions={<Button onClick={save} disabled={saving} className="active:scale-[0.98]" data-testid="smtp-save-button">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva impostazioni
        </Button>} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          {isAdmin && <MailboxSection form={form} set={set} reload={reload} />}
          {isAdmin && <CompanySection form={form} set={set} />}
          <SediSection form={form} set={set} />
          <AutomationSection form={form} set={set} isAdmin={isAdmin} />
          {isAdmin && <PdfSection form={form} set={set} />}
          {isAdmin && <AutoSendSection form={form} set={set} />}
          {isAdmin && <TemplateSection form={form} set={set} />}
        </div>
        <div className="space-y-6 lg:col-span-4">
          {isAdmin && <UsersSection />}
          {isAdmin && <TestEmailCard configured={form.smtp_configured} />}
          <PasswordCard />
        </div>
      </div>
    </>
  );
}
